import axios from 'axios'

const connectInflight = new Map()
const workspaceInflight = new Map()

// entry='research'：从研究空间进入，让 xBIOS 启动时停在项目欢迎页，不先恢复上次的项目。
export function buildOpenCodeAppUrl({ origin, hostname, token, directorySlug, sessionId, entry, notificationNavigation }) {
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const base = isLocalhost ? 'http://localhost:4080/' : `${origin}/opencode/`
  const safeSegment = value => typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)
  const safeDirectory = safeSegment(directorySlug)
  const safeSession = safeSegment(sessionId) && sessionId !== 'persisted-report'
  const target = safeDirectory
    ? `${base}${directorySlug}/session${safeSession ? `/${sessionId}` : ''}`
    : base
  const query = []
  if (token) query.push(`gutdb_token=${encodeURIComponent(token)}`)
  if (entry === 'research') query.push('gutdb_entry=research')
  const notebook = notificationNavigation?.navigation?.target
  if (notebook?.type === 'NOTEBOOK') {
    const requestId = notificationNavigation.requestId
    if (typeof requestId !== 'string' || !/^[A-Za-z0-9-]{1,80}$/.test(requestId)
        || !safeDirectory || !safeSession || notificationNavigation.navigation.directorySlug !== directorySlug
        || notebook.sessionId !== sessionId || notebook.section !== 'runtime-ended'
        || typeof notebook.objectId !== 'string' || !/^[a-f0-9]{48}$/.test(notebook.objectId)
        || typeof notebook.version !== 'string' || !/^[1-9]\d*$/.test(notebook.version)
        || !Number.isSafeInteger(Number(notebook.version))) throw new Error('原 Notebook 运行记录无效，请从消息重新打开')
    query.push(`notificationNavigation=${encodeURIComponent(requestId)}`,
      `notificationNotebookRuntime=${notebook.objectId}`, `notificationNotebookGeneration=${notebook.version}`)
  }
  return query.length ? `${target}?${query.join('&')}` : target
}

// /api/opencode/connect 由后端阻塞等待实例冷启动完成。并发调用共享同一请求，
// 避免 App.vue 预热与 aiChatPage 各自 connect 撞车（服务端串行处理会让第二个请求翻倍等待）。
//
// timeout 默认 100s：后端 OpenCodeInstanceService.callManager() 调 Instance Manager
// 的 HTTP 超时是 60s，前端超时若也设 60s，一旦冷启动接近 60s，后端还要再花几百毫秒
// 处理返回值/序列化响应，前端必然先一步超时报错（表现为"timeout of 60000ms exceeded"，
// 但刷新页面重试就好——因为那时后端早就跑完了）。前端超时需明显大于后端链路超时才不会
// 出现这种"最后一步抢跑"的假失败。Nginx /api/ 的 proxy_read_timeout 是 120s，100s 仍在其内。
function connectIdentity(token, explicit) {
  if (explicit !== undefined && explicit !== '') return `identity:${explicit}`
  try {
    if (localStorage.getItem('token') === token) {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user.identityId !== undefined) return `identity:${user.identityId}`
    }
  } catch { /* Storage unavailable: keep credential isolation. */ }
  return `credential:${token || ''}`
}

export function connectOpenCode(token, { timeout = 100000, signal, identityId } = {}) {
  const key = connectIdentity(token, identityId)
  if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'))
  let entry = connectInflight.get(key)
  if (!entry) {
    entry = { abort: new AbortController(), consumers: new Set(), settled: false }
    entry.promise = axios.get('/api/opencode/connect', {
      headers: { Authorization: `Bearer ${token}` }, timeout: 100000,
      signal: entry.abort.signal, skipAuthRefresh: true,
    }).finally(() => {
      entry.settled = true
      if (connectInflight.get(key) === entry) connectInflight.delete(key)
    })
    connectInflight.set(key, entry)
  }
  // Every caller retains its own deadline/cancellation. The shared HTTP request
  // is cancelled only after all consumers leave, including an App prewarm.
  return new Promise((resolve, reject) => {
    const consumer = {}
    entry.consumers.add(consumer)
    let done = false
    const finish = (fn, value) => {
      if (done) return
      done = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', aborted)
      entry.consumers.delete(consumer)
      if (!entry.settled && !entry.consumers.size) {
        entry.abort.abort()
        if (connectInflight.get(key) === entry) connectInflight.delete(key)
      }
      fn(value)
    }
    const aborted = () => finish(reject, new DOMException('Aborted', 'AbortError'))
    const timer = setTimeout(() => finish(reject, Object.assign(new Error('Connection timeout'), { code: 'ECONNABORTED' })), timeout)
    signal?.addEventListener('abort', aborted, { once: true })
    entry.promise.then(value => finish(resolve, value), error => finish(reject, error))
  })
}

const safeWorkspaceRelativePath = value => {
  if (typeof value !== 'string' || !value.length
      || value.startsWith('/') || value.endsWith('/')
      || value.includes('\\') || value.includes('\0')) return false
  return value.split('/').every(segment => segment.length > 0 && segment !== '.' && segment !== '..')
}

export async function getOpenCodeUserWorkspace(token) {
  if (!token) throw new Error('登录状态已失效，请重新登录')
  await connectOpenCode(token)
  if (!workspaceInflight.has(token)) {
    const pending = axios.get('/opencode/global/gutdb-user', {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-gutdb-token': token,
      },
      timeout: 30000,
    }).then(response => {
      const workspace = response.data || {}
      if (workspace.gutdb !== true
          || typeof workspace.directory !== 'string' || !workspace.directory
          || typeof workspace.username !== 'string' || !workspace.username) {
        throw new Error('无法识别当前个人工作区，请重新登录')
      }
      return { directory: workspace.directory.replace(/\/+$/, ''), username: workspace.username }
    }).finally(() => workspaceInflight.delete(token))
    workspaceInflight.set(token, pending)
  }
  return workspaceInflight.get(token)
}

// OpenCode 的写请求以绝对目录头绑定执行项目；绝对根目录只用于“新建一级项目”
// 这类明确的根级管理动作，以及 demo 参数建议；个人数据分析仍传入所选项目目录。
export async function resolveOpenCodeWorkspaceDirectory(token, options = {}) {
  const workspace = await getOpenCodeUserWorkspace(token)
  let relative = options.projectFolder || ''
  if (!relative && options.workspacePath) {
    const segments = String(options.workspacePath).split('/').filter(Boolean)
    if (segments[0] === workspace.username) segments.shift()
    relative = segments[0] || ''
  }
  if (!relative) {
    if (options.allowUserRoot === true) return workspace.directory
    throw new Error('请先选择个人工作区下的项目目录')
  }
  if (!safeWorkspaceRelativePath(relative)) throw new Error('项目目录无效，请重新选择')
  return `${workspace.directory}/${relative}`
}

export const encodeOpenCodeDirectoryHeader = directory => encodeURIComponent(directory)

// The report service returns the base64url-encoded owning project directory.
// OpenCode still validates this directory against the authenticated user's roots.
export function decodeOpenCodeDirectorySlug(slug) {
  if (typeof slug !== 'string' || !/^[A-Za-z0-9_-]+$/.test(slug) || slug.length > 8192) {
    throw new Error('报告目录信息无效，请刷新后重试')
  }
  try {
    const base64 = slug.replace(/-/g, '+').replace(/_/g, '/')
    const bytes = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
    const directory = decodeURIComponent(Array.from(bytes, byte =>
      '%' + byte.charCodeAt(0).toString(16).padStart(2, '0')).join(''))
    if (!directory.startsWith('/') || directory.includes('\\') || directory.includes('\0')
      || directory.split('/').some(part => part === '..')) throw new Error('invalid directory')
    return directory
  } catch {
    throw new Error('报告目录信息无效，请刷新后重试')
  }
}
